<template>
  <layout-private :customer="customer">
  <div class="row">
    <div class="col-md-10 offset-md-1 col-lg-8 offset-lg-2">
      <hr class="hr-salmon">
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="snug">Team</h1>
      </div>
      <p class="loading" v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i> loading team...</p>
      <list-errors :errors="errors" />
      <hr class="hr-dark-color" />

      <div class="table-responsive table-team" v-if="!loading">
        <table class="table table-custom table-striped-custom table-fixed">
          <colgroup>
            <col style="width: 40%;" />
            <col style="width: 30%;" />
            <col style="width: 30%;" />
          </colgroup>
          <thead>
            <tr>
              <th>Name</th>
              <th>Initials</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <template v-if="editingUserId === user.id">
                <td><input v-model="editUser.name" type="text" class="form-control" placeholder="Name" /></td>
                <td><input v-model="editUser.initials" type="text" class="form-control" placeholder="Initials" /></td>
                <td class="text-right">
                  <button @click="saveEditUser(user.id)" class="btn btn-icon btn-save" :disabled="savingUser"><i class="fas fa-check"></i></button>
                  <button @click="cancelEditUser" class="btn btn-icon btn-cancel"><i class="far fa-ban"></i></button>
                </td>
              </template>
              <template v-else>
                <td>
                    <span class="badge badge-custom badge-tiny badge-custom-center" v-if="user.hidden">hidden </span>
                    <span v-if="user.name">{{ user.name }}</span>
                    <span class="user-dim" v-else><small><em>admin user</em></small></span>
                </td>
                <td>
                  <span v-if="user.initials">{{ user.initials }}</span>
                  <span class="user-dim" v-else><small><em>no initials</em></small></span>
                </td>
                <td class="text-right">
                  <template v-if="deletingUserId === user.id">
                    <button @click="deleteUser(user.id)" class="btn btn-link btn-delete-confirm"><small>confirm delete</small></button>
                    <button @click="deletingUserId=null" class="btn btn-link btn-cancel"><small>cancel</small></button>
                  </template>
                  <template v-else>
                    <button
                      @click="startEditUser(user)"
                      class="btn btn-icon btn-cancel"
                      :disabled="user.authorized_location"
                    ><i class="far fa-edit"></i></button>
                    <button
                      @click="deletingUserId=user.id"
                      class="btn btn-icon btn-cancel"
                      :disabled="user.authorized_location"
                    ><i class="far fa-times"></i></button>
                    <button
                      @click="toggleHidden(user)"
                      class="btn btn-icon btn-toggle-hidden"
                      :class="{ 'is-hidden': user.hidden }"
                      :disabled="user.authorized_location"
                    ><i class="far fa-eye-slash"></i></button>
                  </template>
                </td>
              </template>
            </tr>
            <tr v-if="addingUser">
              <td><input v-model="newUser.name" type="text" class="form-control" placeholder="Name" /></td>
              <td><input v-model="newUser.initials" type="text" class="form-control" placeholder="Initials" /></td>
              <td class="text-right">
                <button @click="saveNewUser" class="btn btn-icon btn-save" :disabled="savingUser"><i class="fas fa-check"></i></button>
                <button @click="cancelAddUser" class="btn btn-icon btn-cancel"><i class="far fa-times"></i></button>
              </td>
            </tr>
            <tr v-if="!users.length && !addingUser">
              <td colspan="3"><em>No team members found.</em></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="row row-table-footer" v-if="!loading">
        <div class="col-12 text-right">
          <button v-if="!addingUser" @click="startAddUser" class="btn btn-green-fade btn-icon-left"><i class="far fa-fw fa-plus-square"></i>Add User</button>
        </div> <!-- /.col-12 -->
      </div>
    </div>
  </div>
  </layout-private>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import LayoutPrivate from '../components/LayoutPrivate.vue'
import ListErrors from '../components/ListErrors.vue'

export default {
  props: ['customer'],
  components: {
    LayoutPrivate, ListErrors,
  },
  data() {
    return {
      loading: false,
      errors: [],
      users: [],
      addingUser: false,
      savingUser: false,
      deletingUserId: null,
      editingUserId: null,
      newUser: {
        name: '',
        initials: '',
      },
      editUser: {
        name: '',
        initials: '',
      },
    }
  },
  computed: {
    ...mapGetters(['custom_claims']),
    customerId() {
      return this.custom_claims?.customerId || null
    },
  },
  watch: {
    custom_claims(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadUsers()
      }
    },
  },
  methods: {
    usersCollectionRef() {
      if (!this.customerId) {
        return null
      }

      return Vue.firestore
        .collection('customers')
        .doc(this.customerId)
        .collection('users')
    },
    mapUserDoc(doc) {
      const data = doc.data() || {}
      return {
        id: doc.id,
        name: data.name || '',
        initials: data.initials || '',
        authorized_location: data.authorized_location || null,
        hidden: data.hidden || false,
      }
    },
    async loadUsers() {
      this.loading = true
      this.errors = []

      try {
        const collectionRef = this.usersCollectionRef()
        if (!collectionRef) {
          this.users = []
          return
        }

        const snapshot = await collectionRef.get()
        this.users = this.sortUsers(snapshot.docs.map((doc) => this.mapUserDoc(doc)))
      } catch (err) {
        console.error('Failed to load team:', err)
        this.errors = ['Failed to load team. Please try again.']
      } finally {
        this.loading = false
      }
    },
    startAddUser() {
      this.deletingUserId = null
      this.editingUserId = null
      this.newUser = { name: '', initials: '' }
      this.addingUser = true
    },
    cancelAddUser() {
      this.addingUser = false
      this.newUser = { name: '', initials: '' }
      this.errors = []
    },
    startEditUser(user) {
      if (user.authorized_location) {
        this.errors = ["Can't edit a user with an assigned location."]
        return
      }

      this.addingUser = false
      this.deletingUserId = null
      this.editUser = { name: user.name, initials: user.initials }
      this.editingUserId = user.id
    },
    cancelEditUser() {
      this.editingUserId = null
      this.editUser = { name: '', initials: '' }
      this.errors = []
    },
    async saveEditUser(userId) {
      const name = this.editUser.name.trim()
      const initials = this.editUser.initials.trim()

      if (!name || !initials) {
        this.errors = ['Please enter both a name and initials.']
        return
      }

      const user = this.users.find((user) => user.id === userId)
      if (user && user.authorized_location) {
        this.errors = ["Can't edit a user with an assigned location."]
        this.cancelEditUser()
        return
      }

      this.savingUser = true
      this.errors = []

      try {
        const collectionRef = this.usersCollectionRef()
        if (!collectionRef) {
          return
        }

        await collectionRef.doc(userId).update({ name, initials })
        const user = this.users.find((user) => user.id === userId)
        if (user) {
          user.name = name
          user.initials = initials
        }
        this.users = this.sortUsers(this.users)
        this.cancelEditUser()
      } catch (err) {
        console.error('Failed to update user:', err)
        this.errors = ['Failed to update user. Please try again.']
      } finally {
        this.savingUser = false
      }
    },
    async saveNewUser() {
      const name = this.newUser.name.trim()
      const initials = this.newUser.initials.trim()

      if (!name || !initials) {
        this.errors = ['Please enter both a name and initials.']
        return
      }

      this.savingUser = true
      this.errors = []

      try {
        const collectionRef = this.usersCollectionRef()
        if (!collectionRef) {
          return
        }

        const docRef = await collectionRef.add({ name, initials })
        this.users.push({ id: docRef.id, name, initials, authorized_location: null, hidden: false })
        this.users = this.sortUsers(this.users)
        this.cancelAddUser()
      } catch (err) {
        console.error('Failed to add user:', err)
        this.errors = ['Failed to add user. Please try again.']
      } finally {
        this.savingUser = false
      }
    },
    async deleteUser(userId) {
      this.errors = []

      const user = this.users.find((user) => user.id === userId)
      if (user && user.authorized_location) {
        this.errors = ["Can't delete a user with an assigned location."]
        this.deletingUserId = null
        return
      }

      try {
        const collectionRef = this.usersCollectionRef()
        if (!collectionRef) {
          return
        }

        await collectionRef.doc(userId).delete()
        this.users = this.users.filter((user) => user.id !== userId)
        this.deletingUserId = null
      } catch (err) {
        console.error('Failed to delete user:', err)
        this.errors = ['Failed to delete user. Please try again.']
      }
    },
    async toggleHidden(user) {
      this.errors = []

      if (user.authorized_location) {
        this.errors = ["Can't hide a user with an assigned location."]
        return
      }

      const hidden = !user.hidden

      try {
        const collectionRef = this.usersCollectionRef()
        if (!collectionRef) {
          return
        }

        await collectionRef.doc(user.id).update({ hidden })
        user.hidden = hidden
        this.users = this.sortUsers(this.users)
      } catch (err) {
        console.error('Failed to update user:', err)
        this.errors = ['Failed to update user. Please try again.']
      }
    },
    sortUsers(users) {
      const sortRank = (user) => {
        if (user.authorized_location) return 2
        if (user.hidden) return 1
        return 0
      }
      return [...users].sort((left, right) => {
        const rankDiff = sortRank(left) - sortRank(right)
        if (rankDiff !== 0) {
          return rankDiff
        }
        return left.name.localeCompare(right.name)
      })
    },
  },
  mounted() {
    this.loadUsers()
  },
}
</script>
<style lang="less" scoped>
@import "../assets/less/variables.less";

.row-table-footer {
  margin-top: 10px;
}
.table-team .table-fixed {
  table-layout: fixed;
}
.table-team {
  .btn-icon {
    padding: 0;
    margin-left: 10px;
  }
  .badge {
    margin-right: 5px;
  }
}
.btn-delete-confirm {
  margin-right: 10px;
}
.btn:disabled .far,
.btn:disabled .fas {
  transition: none !important;
  transform: rotate(0deg) !important;
  cursor: not-allowed !important;
}
.btn-icon:disabled {
  opacity: 0.5 !important;
}
.btn-toggle-hidden i {
  opacity: 0.5;
}
.btn-toggle-hidden:hover:not(:disabled) i {
  color: @color-green;
  opacity: 1;
}
.btn-toggle-hidden.is-hidden i {
  opacity: 1;
}
.btn-toggle-hidden.is-hidden:hover:not(:disabled) i {
  color: @error-color;
}
.btn-salmon:disabled:hover {
  opacity: 0.5 !important;
}
.user-dim {
  color: @dark-color;
  opacity: 0.5;
}
</style>
